import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { optimizeImageUrlFillSquare } from "@faire/web--source/common/images/optimizeImages";
import { useLocalization } from "@faire/web--source/common/localization/useLocalization";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { trackEventOnClick } from "@faire/web--source/common/trackEvent";
import { Button } from "@faire/web--source/slate/Button/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { observer } from "mobx-react";
import React from "react";
import styled, { css } from "styled-components";
import { Carousel as CarouselBase } from "@faire/retailer/components/Carousel/Carousel";
import { blogPage } from "@faire/retailer-visitor-shared/paths/index";
var TestimonialCarouselBase = /*#__PURE__*/ React.forwardRef(function(props, ref) {
    var backgroundColor = props.backgroundColor;
    var maxHeight = useResponsiveValue({
        mobileAndAbove: undefined,
        tabletAndAbove: "400px"
    });
    var isMobile = useViewport().isMobile;
    var _useLocalization = useLocalization(), locale = _useLocalization.intl.locale;
    var _props_items;
    return /*#__PURE__*/ _jsx(Wrapper, {
        $backgroundColor: backgroundColor,
        className: props.className,
        ref: ref,
        children: /*#__PURE__*/ _jsxs(Column, {
            align: "center",
            justify: "space-evenly",
            style: {
                padding: "".concat(getSpacing("3x", 40)),
                height: "100%",
                maxHeight: maxHeight
            },
            children: [
                props.withRetailerCount ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(Header, {
                            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "Jm0BmC",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "TRUSTED BY HUNDREDS OF THOUSANDS OF RETAILERS"
                                    }
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            size: "3x"
                        })
                    ]
                }) : null,
                /*#__PURE__*/ _jsx(Carousel, {
                    name: props.name,
                    itemsPerSlide: 1,
                    padItems: true,
                    noWrap: false,
                    renderItem: function(testimonial, index) {
                        return /*#__PURE__*/ _jsx(TestimonialTile, {
                            testimonial: testimonial,
                            eventPrefix: props.eventPrefix,
                            maxContentWidth: props.maxContentWidth,
                            hideStoryLink: props.hideStoryLink,
                            isMobile: isMobile
                        }, index);
                    },
                    items: (_props_items = props.items) !== null && _props_items !== void 0 ? _props_items : getTestimonials(locale)
                })
            ]
        })
    });
});
export var TestimonialCarousel = observer(TestimonialCarouselBase);
var TestimonialTile = function(param) {
    var testimonial = param.testimonial, eventPrefix = param.eventPrefix, maxContentWidth = param.maxContentWidth, hideStoryLink = param.hideStoryLink, isMobile = param.isMobile;
    var isSSR = useIsSSR();
    return /*#__PURE__*/ _jsxs(Column, {
        align: "center",
        justify: "center",
        children: [
            /*#__PURE__*/ _jsx(Avatar, {
                $avatarImageUrl: testimonial.avatarImageUrl,
                $isSSR: isSSR
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "2x"
            }),
            /*#__PURE__*/ _jsx(Content, {
                $maxContentWidth: maxContentWidth,
                children: isMobile && testimonial.mobileContent ? testimonial.mobileContent : testimonial.content
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "3x"
            }),
            /*#__PURE__*/ _jsx(ReviewerInfo, {
                children: testimonial.reviewerInfo
            }),
            testimonial.blogCta && !hideStoryLink ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "2x"
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        variant: "tertiary",
                        linkTo: testimonial.blogCta,
                        onClick: trackEventOnClick("".concat(eventPrefix).concat(testimonial.reviewerInfo)),
                        children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "yospXm",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Read Their Story"
                                }
                            ]
                        })
                    })
                ]
            }) : null
        ]
    });
};
var getTestimonials = function(locale) {
    return [
        {
            avatarImageUrl: "https://cdn.faire.com/fastly/72f810f95c17f7e377a96d5fa37775a7552c47cfd0cf40074a639b4d80803e05.png",
            content: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "7f0pOH",
                defaultMessage: [
                    {
                        type: 0,
                        value: "\u201CIf you aren\u2019t using Faire, you\u2019re missing a great opportunity. Start today and deal with a very professional company helping all shopkeepers buy quality products from independent makers and artisans.\u201D"
                    }
                ]
            }),
            reviewerInfo: "Cindy Davenport, Cinport Floral Studio & Gift Designs by Cinport"
        },
        {
            avatarImageUrl: "https://cdn.faire.com/fastly/861cfd4023eff50d1fa48db22cbfdf4ce59a05d36634e7aafe86ca022c7bee07.jpeg",
            content: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "lJy7n5",
                defaultMessage: [
                    {
                        type: 0,
                        value: "\u201CWe\u2019ve had such a successful experience with Faire, bringing in new artists and connecting with Shopify; it\u2019s probably the most flawless system we\u2019ve used.\u201D"
                    }
                ]
            }),
            reviewerInfo: "Kelly Marie Thompson, Fleur Inc.",
            blogCta: "".concat(blogPage(locale), "/thestorefront/self-taught-chicago-florist-shares-experience-with-reopening-curbside/")
        },
        {
            avatarImageUrl: "https://cdn.faire.com/fastly/0ded13b33fd5d5c505deb0bd98ec2fb3bc283d2fb09d10ebd82995c4bd326df9.jpeg",
            content: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "lwalu9",
                defaultMessage: [
                    {
                        type: 0,
                        value: "\u201CI love Faire because I don\u2019t have to go to trade shows. Faire\u2019s website is 100% more user-friendly and better designed than other wholesale sites.\u201D"
                    }
                ]
            }),
            reviewerInfo: "John Frechette, MADE",
            blogCta: "".concat(blogPage(locale), "/thestorefront/how-this-wyoming-retailer-is-creating-community-during-crisis/")
        },
        {
            avatarImageUrl: "https://cdn.faire.com/fastly/4a1b2e96d3f7904904f8a5bf9d5fc67ff3a6ddb3bc7fbcd0b7b7c6dda81548f3.jpeg",
            content: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "Q+tdHb",
                defaultMessage: [
                    {
                        type: 0,
                        value: "\u201CFaire makes things way simpler. We\u2019ve migrated a number of our producers to the platform and discovered a bunch of new brands that are a great fit for our store.\u201D"
                    }
                ]
            }),
            reviewerInfo: "Mat Pond, The Epicurean Trader",
            blogCta: "".concat(blogPage(locale), "/thestorefront/san-francisco-specialty-grocery-adapts-to-being-essential/")
        }
    ];
};
var Wrapper = styled(Column).attrs({
    align: "center",
    justify: "center"
}).withConfig({
    componentId: "sc-c4edf4ba-0"
})([
    "",
    " width:100%;"
], function(props) {
    return props.$backgroundColor ? css([
        "background-color:",
        ";"
    ], props.$backgroundColor) : css([
        "background-color:",
        ";"
    ], Decorative.tint.neutral);
});
var Header = styled(Typography).attrs({
    color: Core.text.subdued,
    align: "center"
}).withConfig({
    componentId: "sc-c4edf4ba-1"
})([
    "&&&{letter-spacing:1.25px;}"
]);
var Carousel = styled(CarouselBase).withConfig({
    componentId: "sc-c4edf4ba-2"
})([
    "max-width:1008px;"
]);
var Avatar = styled.div.withConfig({
    componentId: "sc-c4edf4ba-3"
})([
    "width:72px;height:72px;border-radius:100%;background-position:center;background-size:cover;background-image:url(",
    ");"
], function(props) {
    return optimizeImageUrlFillSquare({
        url: props.$avatarImageUrl
    }, props.$isSSR, 72);
});
export var Content = styled(Typography).attrs({
    variant: "pageHeaderSerifRegular",
    forwardedAs: "h3"
}).withConfig({
    componentId: "sc-c4edf4ba-4"
})([
    "text-align:center;",
    ""
], function(props) {
    return props.$maxContentWidth ? css([
        "max-width:",
        "px;"
    ], props.$maxContentWidth) : "";
});
export var ReviewerInfo = styled(Typography).attrs({
    align: "center"
}).withConfig({
    componentId: "sc-c4edf4ba-5"
})([
    ""
]);
